/**
 * AgentSummaryTable
 * Aggregated agent table — one row per agent (summed across tenants).
 * Matches the visual style of TrendingAgentsBoard.
 * Columns: #, ID, agent, creator, conversations, users, input/output/cached tokens,
 *          cost, sparkline trend, and delta badges.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { Select, Skeleton, Tooltip, Typography, theme } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { AgentStat, TimeseriesPoint } from '../../types/insights';
import { useChartTheme, PALETTE } from '../../utils/chartTheme';
import {
  renderAgentOption,
  agentFilterOption,
  AgentPlaceholder,
  AGENT_SELECT_STYLE,
  AGENT_DROPDOWN_STYLE,
} from '../../utils/selectRenderers';

const { Text } = Typography;

const BLUE = '#3b82f6';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

// ─── Shared sub-components ────────────────────────────────────────────────────

interface BadgeTheme {
  mutedText: string;
  positiveBg: string;
  positiveText: string;
  negativeBg: string;
  negativeText: string;
  rankBadgeBg: string;
  rankBadgeText: string;
}

function DeltaBadge({ pct, bt }: { pct: number | null; bt: BadgeTheme }) {
  const { t } = useLanguage();
  if (pct === null || (pct !== 999 && Math.abs(pct) < 1)) {
    return <span style={{ fontSize: 11, color: bt.mutedText, fontWeight: 500, whiteSpace: 'nowrap' }}>—</span>;
  }
  if (pct === 999) {
    return (
      <span style={{ fontSize: 11, fontWeight: 600, color: bt.positiveText, whiteSpace: 'nowrap' }}>
        {t('insights.pulse.newBadge')}
      </span>
    );
  }
  const positive = pct >= 0;
  const display = Math.abs(pct) >= 10 ? Math.round(Math.abs(pct)) : Math.abs(pct).toFixed(1);
  return (
    <span style={{ fontSize: 11, fontWeight: 600, color: positive ? bt.positiveText : bt.negativeText, whiteSpace: 'nowrap' }}>
      {positive ? '↑' : '↓'} {display}%
    </span>
  );
}

function Sparkline({ data, color = BLUE }: { data: number[]; color?: string }) {
  const W = 60;
  const H = 24;
  if (data.length < 2) return <div style={{ width: W, height: H }} />;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * (W - 2) + 1;
    const y = H - 2 - ((v / max) * (H - 4)) + 1;
    return `${x},${y}`;
  });
  return (
    <svg width={W} height={H} style={{ display: 'block', overflow: 'visible' }}>
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function RankBadge({ rank, bt }: { rank: number; bt: BadgeTheme }) {
  if (rank === 1) return <span style={{ fontSize: 16 }}>🥇</span>;
  if (rank === 2) return <span style={{ fontSize: 16 }}>🥈</span>;
  if (rank === 3) return <span style={{ fontSize: 16 }}>🥉</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: bt.rankBadgeBg, color: bt.rankBadgeText, fontSize: 11, fontWeight: 700 }}>
      {rank}
    </span>
  );
}

// ─── Row type ─────────────────────────────────────────────────────────────────

interface AgentSummaryRow {
  chatbot_id: string;
  chatbot_name: string;
  creator_name: string;
  conversations: number;
  messages: number;
  users: number;
  input_tokens: number;
  output_tokens: number;
  cached_tokens: number;
  cost_usd: number;
  sparklineData: number[];
  delta_conversations: number | null;
  delta_users: number | null;
  delta_tokens: number | null;
}

type SortField = 'chatbot_name' | 'creator_name' | 'conversations' | 'messages' | 'users'
               | 'input_tokens' | 'output_tokens' | 'cached_tokens' | 'cost_usd'
               | 'delta_conversations' | 'delta_users' | 'delta_tokens';

function buildSummaryRows(
  stats: AgentStat[],
  timeseries: TimeseriesPoint[],
): AgentSummaryRow[] {
  // Aggregate stats by chatbot_id
  const byBot = new Map<string, Omit<AgentSummaryRow, 'sparklineData' | 'delta_conversations' | 'delta_users' | 'delta_tokens'>>();
  for (const s of stats) {
    const existing = byBot.get(s.chatbot_id);
    if (existing) {
      existing.conversations += s.total_conversations;
      existing.messages += s.total_messages;
      existing.users += s.active_users;
      existing.input_tokens += s.total_input_tokens;
      existing.output_tokens += s.total_output_tokens;
      existing.cached_tokens += s.total_cached_tokens;
      existing.cost_usd += s.total_cost_usd;
    } else {
      byBot.set(s.chatbot_id, {
        chatbot_id: s.chatbot_id,
        chatbot_name: s.chatbot_name,
        creator_name: s.creator_name || '—',
        conversations: s.total_conversations,
        messages: s.total_messages,
        users: s.active_users,
        input_tokens: s.total_input_tokens,
        output_tokens: s.total_output_tokens,
        cached_tokens: s.total_cached_tokens,
        cost_usd: s.total_cost_usd,
      });
    }
  }

  // Aggregate timeseries by chatbot_id (sum across tenants per date)
  const tsByBot = new Map<string, Map<string, { conversations: number; users: number; input_tokens: number }>>();
  for (const pt of timeseries) {
    if (!pt.chatbot_id) continue;
    if (!tsByBot.has(pt.chatbot_id)) tsByBot.set(pt.chatbot_id, new Map());
    const dateMap = tsByBot.get(pt.chatbot_id)!;
    const existing = dateMap.get(pt.date);
    if (existing) {
      existing.conversations += pt.conversations;
      existing.users += (pt.users || 0);
      existing.input_tokens += (Number(pt.input_tokens) || 0);
    } else {
      dateMap.set(pt.date, {
        conversations: pt.conversations,
        users: pt.users || 0,
        input_tokens: Number(pt.input_tokens) || 0,
      });
    }
  }

  const allDates = Array.from(new Set(timeseries.map(p => p.date))).sort();
  const mid = Math.floor(allDates.length / 2);
  const firstDates = new Set(allDates.slice(0, mid));
  const secondDates = new Set(allDates.slice(mid));

  const pct = (first: number, second: number, newThreshold = 5): number | null => {
    if (first === 0 && second === 0) return null;
    if (first === 0 && second >= newThreshold) return 999;
    if (first === 0 && second > 0) return null;
    if (second === 0 && first > 0) return -100;
    return ((second - first) / first) * 100;
  };

  return Array.from(byBot.values()).map(stat => {
    const dateMap = tsByBot.get(stat.chatbot_id) ?? new Map();
    const sortedDates = Array.from(dateMap.keys()).sort();
    const sparklineData = sortedDates.slice(-10).map(d => dateMap.get(d)!.conversations);

    let firstConvos = 0, secondConvos = 0;
    let firstUsers = 0, secondUsers = 0;
    let firstTokens = 0, secondTokens = 0;
    for (const [date, val] of dateMap.entries()) {
      if (firstDates.has(date)) { firstConvos += val.conversations; firstUsers += val.users; firstTokens += val.input_tokens; }
      if (secondDates.has(date)) { secondConvos += val.conversations; secondUsers += val.users; secondTokens += val.input_tokens; }
    }

    return {
      ...stat,
      sparklineData,
      delta_conversations: pct(firstConvos, secondConvos),
      delta_users: pct(firstUsers, secondUsers),
      delta_tokens: pct(firstTokens, secondTokens),
    };
  });
}

function sortDelta(a: number | null, b: number | null, asc: boolean): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return asc ? a - b : b - a;
}

// ─── Props & component ────────────────────────────────────────────────────────

export interface AgentSummaryTableProps {
  agentStats: AgentStat[];
  agentTimeseries: TimeseriesPoint[];
  loading: boolean;
}

export function AgentSummaryTable({ agentStats, agentTimeseries, loading }: AgentSummaryTableProps) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const ct = useChartTheme();

  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>('cost_usd');
  const [sortAsc, setSortAsc] = useState(false);

  // Reset local filters when upstream data changes (global scope filter applied)
  useEffect(() => {
    setSelectedAgents([]);
  }, [agentStats]);

  // Build unique agent options from stats — group by name so shared agents appear once
  const agentOptions = useMemo(() => {
    const byName = new Map<string, string[]>();
    for (const s of agentStats) {
      const ids = byName.get(s.chatbot_name) || [];
      if (!ids.includes(s.chatbot_id)) ids.push(s.chatbot_id);
      byName.set(s.chatbot_name, ids);
    }
    return Array.from(byName.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, ids]) => ({ value: ids.join(','), label: name }));
  }, [agentStats]);

  const rows = useMemo(() => {
    const built = buildSummaryRows(agentStats, agentTimeseries);
    let filtered = built;
    if (selectedAgents.length > 0) {
      const ids = new Set(selectedAgents.flatMap(id => id.split(',')));
      filtered = filtered.filter(r => ids.has(r.chatbot_id));
    }
    return [...filtered].sort((a, b) => {
      if (sortField === 'delta_conversations') return sortDelta(a.delta_conversations, b.delta_conversations, sortAsc);
      if (sortField === 'delta_users') return sortDelta(a.delta_users, b.delta_users, sortAsc);
      if (sortField === 'delta_tokens') return sortDelta(a.delta_tokens, b.delta_tokens, sortAsc);
      const av = a[sortField]; const bv = b[sortField];
      if (typeof av === 'string' && typeof bv === 'string') return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortAsc ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
  }, [agentStats, agentTimeseries, selectedAgents, sortField, sortAsc]);

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortAsc(prev => !prev);
    else { setSortField(field); setSortAsc(false); }
  };

  const thStyle: React.CSSProperties = {
    padding: '8px 12px',
    fontSize: 11,
    fontWeight: 700,
    color: ct.tableHeaderText,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    background: ct.tableHeaderBg,
    borderBottom: `1px solid ${token.colorBorderSecondary}`,
    whiteSpace: 'nowrap',
    textAlign: 'center',
  };

  const SortHeader = ({ field, children, tip }: { field: SortField; children: React.ReactNode; tip?: string }) => (
    <th onClick={() => handleSort(field)} style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}>
      {children}
      {tip && (
        <Tooltip title={tip}>
          <InfoCircleOutlined style={{ marginInlineStart: 4, fontSize: 10, color: ct.mutedText, cursor: 'help' }} />
        </Tooltip>
      )}{' '}
      {sortField === field ? (sortAsc ? '↑' : '↓') : <span style={{ color: ct.legendHidden }}>↕</span>}
    </th>
  );

  const Th = ({ children, tip }: { children: React.ReactNode; tip?: string }) => (
    <th style={thStyle}>
      {children}
      {tip && (
        <Tooltip title={tip}>
          <InfoCircleOutlined style={{ marginInlineStart: 4, fontSize: 10, color: ct.mutedText, cursor: 'help' }} />
        </Tooltip>
      )}
    </th>
  );

  const Td = ({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' | 'center' }) => (
    <td style={{ padding: '9px 12px', fontSize: 13, borderBottom: `1px solid ${token.colorBorderSecondary}`, textAlign: align, whiteSpace: 'nowrap' }}>
      {children}
    </td>
  );

  const COL_COUNT = 14;

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8, marginBottom: 12 }}>
        <Select
          mode="multiple"
          size="small"
          placeholder={<AgentPlaceholder text={t('insights.pulse.filterByAgent')} />}
          value={selectedAgents}
          onChange={setSelectedAgents}
          options={agentOptions}
          maxTagCount={2}
          allowClear
          showSearch
          filterOption={agentFilterOption}
          style={AGENT_SELECT_STYLE}
          popupMatchSelectWidth={false}
          dropdownStyle={AGENT_DROPDOWN_STYLE}
          optionRender={renderAgentOption}
        />
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', borderRadius: token.borderRadiusLG, border: `1px solid ${token.colorBorderSecondary}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1060 }}>
          <thead>
            <tr>
              <Th tip={t('insights.pulse.tipRank')}>{t('insights.pulse.rank')}</Th>
              <Th tip={t('insights.pulse.tipAgentId')}>{t('insights.pulse.agentId')}</Th>
              <SortHeader field="chatbot_name">{t('insights.pulse.agentName')}</SortHeader>
              <SortHeader field="creator_name">{t('insights.pulse.createdBy')}</SortHeader>
              <SortHeader field="conversations">{t('insights.pulse.conversations')}</SortHeader>
              <SortHeader field="messages" tip={t('insights.pulse.tipMessages')}>{t('insights.pulse.messages')}</SortHeader>
              <SortHeader field="users">{t('insights.pulse.uniqueUsers')}</SortHeader>
              <SortHeader field="input_tokens" tip={t('insights.pulse.tipInputTokens')}>{t('insights.pulse.inputTokens')}</SortHeader>
              <SortHeader field="output_tokens" tip={t('insights.pulse.tipOutputTokens')}>{t('insights.pulse.outputTokens')}</SortHeader>
              <SortHeader field="cached_tokens" tip={t('insights.pulse.tipCachedTokens')}>{t('insights.pulse.cachedTokens')}</SortHeader>
              <Th tip={t('insights.pulse.tipTrend')}>{t('insights.pulse.trend')}</Th>
              <SortHeader field="delta_conversations" tip={t('insights.pulse.tipDeltaConvos')}>{t('insights.pulse.deltaConvos')}</SortHeader>
              <SortHeader field="delta_users" tip={t('insights.pulse.tipDeltaUsers')}>{t('insights.pulse.deltaUsers')}</SortHeader>
              <SortHeader field="delta_tokens" tip={t('insights.pulse.tipDeltaTokens')}>{t('insights.pulse.deltaTokens')}</SortHeader>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: COL_COUNT }).map((_, j) => (
                    <td key={j} style={{ padding: '10px 12px', borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
                      <Skeleton.Input active size="small" style={{ width: 60, height: 16, borderRadius: 4 }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={COL_COUNT} style={{ textAlign: 'center', padding: '40px 0', color: ct.mutedText, fontSize: 13 }}>
                  {t('insights.noAgentDataAvailable')}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={row.chatbot_id} style={{ background: idx % 2 === 0 ? token.colorBgContainer : token.colorBgLayout }}>
                  <Td align="center"><RankBadge rank={idx + 1} bt={ct} /></Td>
                  <Td>
                    <Text type="secondary" copyable={{ text: row.chatbot_id }} style={{ fontSize: 11, fontFamily: 'monospace' }}>{row.chatbot_id.slice(0, 8)}</Text>
                  </Td>
                  <Td>
                    <Text strong style={{ fontSize: 13 }} dir="auto">{row.chatbot_name}</Text>
                  </Td>
                  <Td>
                    <Text type="secondary" style={{ fontSize: 12 }}>{row.creator_name}</Text>
                  </Td>
                  <Td align="center"><Text style={{ fontWeight: 600 }}>{formatNumber(row.conversations)}</Text></Td>
                  <Td align="center"><Text>{formatNumber(row.messages)}</Text></Td>
                  <Td align="center"><Text>{formatNumber(row.users)}</Text></Td>
                  <Td align="center"><Text>{formatNumber(row.input_tokens)}</Text></Td>
                  <Td align="center"><Text>{formatNumber(row.output_tokens)}</Text></Td>
                  <Td align="center"><Text>{formatNumber(row.cached_tokens)}</Text></Td>
                  <Td align="center">
                    <Sparkline data={row.sparklineData} color={PALETTE[idx % PALETTE.length]} />
                  </Td>
                  <Td align="center"><DeltaBadge pct={row.delta_conversations} bt={ct} /></Td>
                  <Td align="center"><DeltaBadge pct={row.delta_users} bt={ct} /></Td>
                  <Td align="center"><DeltaBadge pct={row.delta_tokens} bt={ct} /></Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
